import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Common } from 'src/domains/common/Common';
import { computed, makeObservable } from 'mobx';
import { OpenapiProxyAnonymousSportsResponse200Type } from 'src/api_openapi/generated/openapi_proxy_anonymous_sports';
import { ResourceSSR } from 'src_common/common/mobx-utils/ResourceSSR';
import { getSsrPreloadData } from 'src/utils/getSsrPreloadData';
import { CACHE_MANAGER_KEY } from 'src_server/models/cacheModels';

function allSportsHardcoded(): Array<string> {
    console.warn('Return hardcoded sports list - probably problem with SSR');
    return [
        'handball',
        'horseracingantepost',
        'basketball',
        'boxing',
        'formulaone',
        'rugbyleague',
        'politics',
        'icehockey',
        'horseracing',
        'golf',
        'greyhoundracing',
        'mixedmartialarts',
        'tvspecials',
        'darts',
        'cycling',
        'cricket',
        'tennis',
        'australianrules',
        'americanfootball',
        'snooker',
        'baseball',
        'rugbyunion',
        'football',
        'volleyball',
    ];
}

export class AllSports {
    private readonly allSportsResource: ResourceSSR<OpenapiProxyAnonymousSportsResponse200Type>;
    private readonly allSportWithActiveEvent: ResourceSSR<Array<string>>;

    public static get = AutoWeakMap.create((common: Common) => new AllSports(common));

    private constructor(common: Common) {
        makeObservable(this);

        this.allSportsResource = new ResourceSSR(
            async (): Promise<OpenapiProxyAnonymousSportsResponse200Type> => {
                const response = await common.trpcClient.client.tradingRouter.getAllSports.query();
                if (response.status === 200) {
                    return response.body;
                }

                return [];
            },
            {
                defaultValue: [],
                label: 'allSportsResource',
            }
        );

        this.allSportWithActiveEvent = new ResourceSSR(
            async (): Promise<Array<string>> => {
                const response = await common.trpcClient.client.tradingRouter.filterActiveSports.query();
                if (response.length > 0) {
                    return response.map((item) => item.key);
                }
                return [];
            },
            {
                defaultValue: [],
                label: 'allSportWithActiveEvent',
            }
        );
    }

    /**
     * @deprecated - remove this function
     */
    public getAllSports(): Array<string> | null {
        const allSports = this.allSportsResource.get();

        if (allSports.type === 'ready') {
            return allSports.value.map((item) => item.id);
        }

        return null;
    }

    /**
     * @deprecated - remove this function
     */
    public getSport(sportId: string): string | null {
        const allSports = this.allSportsResource.get();

        if (allSports.type === 'ready') {
            for (const item of allSports.value) {
                if (item.id === sportId) {
                    return item.name;
                }
            }
        }

        return null;
    }

    @computed public get allSports(): OpenapiProxyAnonymousSportsResponse200Type | null {
        const allSports = this.allSportsResource.get();

        if (allSports.type === 'ready') {
            return allSports.value;
        }

        return null;
    }

    @computed public get allSportsWithActiveEvent(): Array<string> | 'loading' {
        const preloadData = getSsrPreloadData();
        const allSportsFromPreloadSsrData =
            preloadData === null ? null : preloadData[CACHE_MANAGER_KEY.ALL_ACTIVE_SPORTS_KEY];

        if (Array.isArray(allSportsFromPreloadSsrData) && allSportsFromPreloadSsrData.length > 0) {
            return allSportsFromPreloadSsrData.map((item) => item.key);
        }

        const allSportWithActiveEvent = this.allSportWithActiveEvent.get();

        if (allSportWithActiveEvent.type === 'ready') {
            if (allSportWithActiveEvent.value.length !== 0) {
                return allSportWithActiveEvent.value;
            }
        }

        if (allSportWithActiveEvent.type === 'loading') {
            return 'loading';
        }
        return allSportsHardcoded();
    }

    @computed public get allSportsMap(): Map<string, string> | null {
        const map = new Map<string, string>();
        const allSports = this.allSportsResource.get();
        if (allSports.type === 'ready') {
            allSports.value.forEach((item) => {
                map.set(item.id, item.name);
            });
            return map;
        }

        return null;
    }
}
